// MAR-130: platform detection, route suppression and dismissal persistence
// for the app download banner
// (client/src/components/common/AppDownloadBanner.tsx).
//
// Kept as a plain module rather than hook-internal state so all of it is
// testable without mounting React, and so the storage decision below is
// documented in one place.
//
// STORAGE CHOICE: localStorage, not a cookie.
//
// The site already has a consent model (client/src/lib/consent.ts) that writes
// a `cc_consent` cookie and shows a hard CookieWall when cookies are blocked,
// so a cookie would have worked. localStorage is the better fit here:
//
//   1. This is a device-local UI preference. It never needs to reach the
//      server, so it should not ride along on every request to
//      capsulechichub.com. A cookie would add bytes to each request and put a
//      user-varying value on the same origin Cloudflare Pages caches.
//   2. It is not a consent question. Storing "this person closed a banner" is
//      strictly functional (ePrivacy: strictly necessary for a service the
//      user requested), so it does not need to be gated on `hasConsent()` and
//      does not belong in the tracking-cookie story.
//
// Every access is wrapped: Safari private browsing and lockdown storage
// settings can make even reading `window.localStorage` throw. We fail OPEN
// (banner shows, dismissal simply does not stick) rather than crash the page.

export type AppPlatform = "ios" | "android" | "other";

const DISMISSED_KEY = "cch.app-banner-dismissed";

/** Shape of the bits of `navigator` we sniff. Injectable so tests can lie. */
export interface PlatformNavigator {
  userAgent: string;
  maxTouchPoints?: number;
}

/**
 * Which store, if any, this visitor can act on.
 *
 * iPadOS 13+ reports a desktop Macintosh user agent, so the touch-point check
 * is the only way to tell an iPad from a Mac. iPhone-only apps still install
 * on iPad, so iPads count as "ios" even though `supportsTablet` is false in
 * mobile/app.json.
 */
export function detectPlatform(
  nav: PlatformNavigator | undefined = typeof navigator !== "undefined"
    ? navigator
    : undefined,
): AppPlatform {
  if (!nav) return "other";
  const ua = nav.userAgent ?? "";

  // Android must be tested first: Android tablets and some Android WebViews
  // put "Mac OS X" in the UA string for compatibility.
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";

  // iPadOS 13+ pretending to be a Mac. Safari is the only browser engine on
  // iPadOS, so a UA that names Chrome or Firefox is a real desktop machine,
  // and a desktop Mac plugged into a touch monitor reports maxTouchPoints > 1
  // exactly like an iPad does. Without the browser check those Macs would be
  // offered the App Store alone, instead of both stores and a QR code for
  // whichever phone the reader has.
  if (
    /Macintosh/i.test(ua) &&
    (nav.maxTouchPoints ?? 0) > 1 &&
    !/Chrome|Chromium|Firefox|Edg\//i.test(ua)
  ) {
    return "ios";
  }

  return "other";
}

/**
 * Routes where the banner must not render, because something else already
 * owns the bottom edge of the viewport.
 *
 * The two public quiz routes stack a mobile "start the quiz" CTA
 * (PublicQuizShell) and a signup-gate footer plus a "See my result" pill
 * (SignupGateOverlay) along the bottom. Those are the primary conversion path
 * from Instagram, and a promo banner covering them would make the quiz
 * unstartable on mobile.
 *
 * Suppression is deliberately chosen over restacking or offsetting. Lifting
 * those surfaces above the banner would leave two bars physically stacked in
 * a short mobile viewport, and offsetting them would couple three components
 * to this banner's measured height. The banner is the lowest-value thing on
 * screen, so the banner yields.
 *
 * Shop's ScrollToTopButton is handled differently: it is one small floating
 * button, so it reads the --app-banner-height custom property and moves up.
 */
const SUPPRESSED_ROUTES = ["/body-shape-quiz", "/colour-analysis-quiz"];

/**
 * MUST be passed a bare pathname, never `pathname + search + hash`.
 *
 * This is safe today only because wouter's useLocation strips the query string
 * and the fragment. Almost all traffic to the quiz routes arrives from
 * Instagram carrying utm_* and fbclid, so a future caller that passes a full
 * URL would stop matching, un-suppress the banner, and hide the quiz CTA for
 * exactly the visitors the suppression exists to protect. It would also look
 * fine in local testing, where nobody types the UTMs.
 */
export function isBannerRouteSuppressed(path: string): boolean {
  // Trailing slashes reach the SPA from the Pages middleware, so normalise.
  const normalised = path.replace(/\/+$/, "") || "/";
  return SUPPRESSED_ROUTES.includes(normalised);
}

/**
 * Crawlers and audit tools. The banner is promotion for people with a phone;
 * to a crawler it is only something drawn over the article it is judging.
 * Google's smartphone crawler sends an Android UA with "Googlebot" appended,
 * so this must be checked before the platform is.
 *
 * A list of names rather than a bare /bot/: "CUBOT" is an Android phone brand.
 */
const CRAWLER_UA =
  /googlebot|google-inspectiontool|storebot-google|adsbot-google|mediapartners-google|bingbot|slurp|duckduckbot|baiduspider|yandexbot|applebot|gptbot|claudebot|perplexitybot|facebookexternalhit|twitterbot|linkedinbot|petalbot|semrushbot|ahrefsbot|lighthouse|crawler|spider/i;

export function isCrawler(userAgent: string | undefined): boolean {
  return CRAWLER_UA.test(userAgent ?? "");
}

/** A Style Hub article: /style-hub/<slug>, with or without the trailing slash. */
const ARTICLE_ROUTE = /^\/style-hub\/[^/]+\/?$/;

/** Bare pathname only, like isBannerRouteSuppressed. */
export function isArticleRoute(path: string): boolean {
  return ARTICLE_ROUTE.test(path);
}

/**
 * Was this page load a reader arriving on an article from outside the site?
 *
 * The banner stays off for that first view, which is the page Google ranks
 * and the one its intrusive interstitial rules judge. Anything that is not
 * our own origin counts as outside, and so does no referrer at all: search
 * apps, in-app browsers and strict referrer policies all strip it, so an
 * empty referrer on an article is far more often search than someone typing
 * a 50-character slug. The quiz intros (/body-shape-quiz and
 * /colour-analysis-quiz) need no rule here because SUPPRESSED_ROUTES already
 * keeps the banner off them on every view.
 */
export function isExternalArticleLanding(
  landingPath: string,
  referrer: string,
  origin: string,
): boolean {
  if (!isArticleRoute(landingPath)) return false;
  if (!referrer) return true;
  try {
    return new URL(referrer).origin !== origin;
  } catch {
    return true;
  }
}

/**
 * Has this device already closed the banner?
 *
 * The stored value is the dismissal timestamp rather than a bare flag, so a
 * re-promotion window is available later without renaming the key: someone
 * who dismissed on launch day, before the app had anything in it, can be asked
 * once more. Pass `maxAgeMs` to use it. Default is forever, which is today's
 * behaviour.
 */
export function isBannerDismissed(
  maxAgeMs: number = Number.POSITIVE_INFINITY,
  now: number = Date.now(),
): boolean {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(DISMISSED_KEY);
  } catch {
    return false;
  }
  if (raw === null) return false;

  const at = Number(raw);
  // Anything unparseable means someone hand-edited the store or an older
  // format is present. Treat it as dismissed: a stray value should never
  // resurrect a banner the person already closed.
  if (!Number.isFinite(at)) return true;

  return now - at < maxAgeMs;
}

/** Remember that this device closed the banner. Best effort. */
export function dismissBanner(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, String(now));
  } catch {
    // Storage unavailable (private mode, quota, lockdown). The banner is
    // hidden for this page view either way; it will come back on the next
    // one, which is a nuisance but not a bug we can fix from here.
  }
}

/** Test-only helper so suites can start from a clean slate. */
export function resetBannerDismissal(): void {
  try {
    window.localStorage.removeItem(DISMISSED_KEY);
  } catch {
    /* nothing to reset */
  }
}
